import type {
  ReleaseStatusCommit,
  ReleaseStatusIssue,
  ReleaseStatusRelease,
} from './types';

import { cn } from 'cn';
import { useTranslation } from 'react-i18next';

import { currentLocale } from '~/utils';

import { EmptyState, withErrorBoundary } from '@vestigas/ui';

import { useReleaseStatusData } from './useReleaseStatusData';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Color-coded status badge matching Linear's state colors */
function StatusBadge({
  status,
  statusColor,
}: {
  status: string;
  statusColor?: string | null;
}) {
  const hex = statusColor ?? '#95a2b3';
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: `${hex}20`,
        color: hex,
      }}
    >
      {status}
    </span>
  );
}

/** Label badge */
function LabelBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-gray-300 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-400">
      {name}
    </span>
  );
}

/** Single issue row */
function IssueRow({
  issue,
  gitlabBaseUrl,
}: {
  issue: ReleaseStatusIssue;
  gitlabBaseUrl?: string | null;
}) {
  const { t } = useTranslation('release');
  const mrUrl =
    gitlabBaseUrl && issue.mrNumber
      ? `${gitlabBaseUrl}/-/merge_requests/${issue.mrNumber}`
      : null;

  return (
    <div className="flex items-start gap-3 border-b border-gray-100 py-2 last:border-b-0 dark:border-gray-700/50">
      {/* Issue ID */}
      <a
        href={`https://linear.app/vestigas/issue/${issue.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 font-mono text-sm text-indigo-600 hover:text-indigo-500 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300"
      >
        {issue.id}
      </a>

      {/* Title + labels + MR link */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {issue.title ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {issue.title}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic dark:text-gray-500">
              No Linear data available
            </p>
          )}
        </div>
        {issue.description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
            {issue.description}
          </p>
        )}
        {issue.missingTestInstructions && (
          <div className="mt-1 flex items-center">
            <span className="-ml-1 inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
              {t(($) => $.releaseStatus.missingTestInstructions)}
            </span>
          </div>
        )}
        {mrUrl && (
          <a
            href={mrUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block font-mono text-xs text-indigo-600 hover:text-indigo-500 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            Merge Request: #{issue.mrNumber}
          </a>
        )}

        {issue.labels && issue.labels.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {issue.labels.toSorted().map((label) => (
              <LabelBadge key={label} name={label} />
            ))}
          </div>
        )}
      </div>

      {/* Status */}
      {issue.status && (
        <StatusBadge status={issue.status} statusColor={issue.statusColor} />
      )}
    </div>
  );
}

/** Commit row for commits without issue reference */
function CommitRow({
  commit,
  gitlabBaseUrl,
}: {
  commit: ReleaseStatusCommit;
  gitlabBaseUrl?: string | null;
}) {
  const commitUrl =
    gitlabBaseUrl && (commit.hashFull ?? commit.hash)
      ? `${gitlabBaseUrl}/-/commit/${commit.hashFull ?? commit.hash}`
      : null;

  return (
    <div className="flex items-center gap-3 border-b border-gray-100 py-1.5 last:border-b-0 dark:border-gray-700/50">
      {commitUrl ? (
        <a
          href={commitUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 font-mono text-xs text-indigo-600 hover:text-indigo-500 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          {commit.hash}
        </a>
      ) : (
        <code className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
          {commit.hash}
        </code>
      )}
      <span className="min-w-0 flex-1 truncate text-sm text-gray-600 dark:text-gray-400">
        {commit.message}
      </span>
      <span
        className="max-w-32 shrink-0 truncate text-xs text-gray-400 dark:text-gray-500"
        title={commit.author ?? undefined}
      >
        {commit.author ?? '—'}
      </span>
      <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
        {commit.date}
      </span>
    </div>
  );
}

/** Compute release readiness for the unreleased section based on Linear issue statuses */
function getReleaseReadiness(
  releases: ReleaseStatusRelease[],
  linearEnriched: boolean,
): 'ready' | 'notReady' | 'unknown' | null {
  const unreleased = releases.find((r) =>
    r.label.toLowerCase().includes('next release'),
  );
  if (!unreleased) return null;

  if (unreleased.issues.length === 0) return 'ready';

  if (!linearEnriched) return 'unknown';

  const allApproved = unreleased.issues.every(
    (i) => i.status?.toLowerCase() === 'user approved',
  );
  return allApproved ? 'ready' : 'notReady';
}

/** Release readiness badge for unreleased section header */
function ReleaseReadyBanner({
  readiness,
  compact = false,
}: {
  readiness: 'ready' | 'notReady' | 'unknown';
  compact?: boolean;
}) {
  const { t } = useTranslation('release');

  const configs: Record<
    'ready' | 'notReady' | 'unknown',
    { label: string; className: string }
  > = {
    ready: {
      label: t(($) => $.releaseStatus.releaseReady),
      className:
        'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300',
    },
    notReady: {
      label: t(($) => $.releaseStatus.notReleaseReady),
      className: 'border-0 bg-red-600 text-white',
    },
    unknown: {
      label: t(($) => $.releaseStatus.releaseReadyUnknown),
      className:
        'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-400',
    },
  };
  const config = configs[readiness];

  return (
    <div
      className={cn(
        'rounded-lg border font-medium',
        config.className,
        compact ? 'px-3 py-1.5 text-sm' : 'px-6 py-4 text-center text-xl',
      )}
    >
      {config.label}
    </div>
  );
}

/** Group issues by project */
function groupIssuesByProject(issues: ReleaseStatusIssue[]) {
  const groups = new Map<string, ReleaseStatusIssue[]>();

  for (const issue of issues) {
    const key = issue.project ?? 'Other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(issue);
  }

  // Sort: named projects first alphabetically, "Other" at the end
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
}

/** A single release section (e.g., "Unreleased" or "1.7.6") */
function ReleaseSection({
  release,
  gitlabBaseUrl,
  releaseReadiness,
}: {
  release: ReleaseStatusRelease;
  gitlabBaseUrl?: string | null;
  releaseReadiness?: 'ready' | 'notReady' | 'unknown' | null;
}) {
  const { t } = useTranslation('release');
  const isNextRelease = release.label.toLowerCase().includes('next release');
  const projectGroups = groupIssuesByProject(release.issues);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {release.label}
          </h2>
          {isNextRelease && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
              {t(($) => $.releaseStatus.unreleased)}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {isNextRelease && releaseReadiness && (
            <ReleaseReadyBanner readiness={releaseReadiness} compact />
          )}
          {release.date && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {Intl.DateTimeFormat(currentLocale(), {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(release.date))}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mb-4 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
        <span>
          {t(($) => $.releaseStatus.issueCount, {
            count: release.issueCount,
          })}
        </span>
        <span>
          {t(($) => $.releaseStatus.commitCount, {
            count: release.commitCount,
          })}
        </span>
      </div>

      {/* Issues grouped by project */}
      {release.issues.length > 0 && (
        <div className="space-y-4">
          {projectGroups.map(([project, issues]) => (
            <div key={project}>
              {projectGroups.length > 1 && (
                <h3 className="text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400">
                  {t(($) => $.releaseStatus.projectGroupHeading, { project })}
                  <span className="ml-1.5 font-normal text-gray-400 dark:text-gray-500">
                    (
                    {t(($) => $.releaseStatus.issueCount, {
                      count: issues.length,
                    })}
                    )
                  </span>
                </h3>
              )}
              {issues.map((issue) => (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  gitlabBaseUrl={gitlabBaseUrl}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Commits without issue */}
      {release.commitsWithoutIssue.length > 0 && (
        <details className="group mt-4" open>
          <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
            {t(($) => $.releaseStatus.commitsWithoutIssue, {
              count: release.commitsWithoutIssue.length,
            })}
          </summary>
          <div className="mt-2">
            {release.commitsWithoutIssue.map((commit) => (
              <CommitRow
                key={commit.hash}
                commit={commit}
                gitlabBaseUrl={gitlabBaseUrl}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type P = ComponentStyling;

/** Small indicator showing live-refresh status */
function RefreshIndicator({
  isRefreshing,
  lastRefreshedAt,
  isLiveRefreshEnabled,
}: {
  isRefreshing: boolean;
  lastRefreshedAt: Date | null;
  isLiveRefreshEnabled: boolean;
}) {
  const { t } = useTranslation('release');

  if (!isLiveRefreshEnabled) {
    return (
      <span className="text-xs text-gray-400 dark:text-gray-500">
        {t(($) => $.releaseStatus.liveRefresh.disabled)}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
      {isRefreshing ? (
        <>
          <span className="size-1.5 animate-pulse rounded-full bg-indigo-400" />
          {t(($) => $.releaseStatus.liveRefresh.refreshing)}
        </>
      ) : lastRefreshedAt ? (
        <>
          <span className="size-1.5 rounded-full bg-green-400" />
          {t(($) => $.releaseStatus.liveRefresh.lastRefreshed, {
            date: Intl.DateTimeFormat(currentLocale(), {
              timeStyle: 'short',
            }).format(lastRefreshedAt),
          })}
        </>
      ) : (
        <>
          <span className="size-1.5 rounded-full bg-green-400" />
          {t(($) => $.releaseStatus.liveRefresh.enabled)}
        </>
      )}
    </span>
  );
}

export const ReleaseStatusList = withErrorBoundary(
  ({ className, style }: P) => {
    const { t } = useTranslation('release');
    const { data, isRefreshing, lastRefreshedAt, isLiveRefreshEnabled } =
      useReleaseStatusData();

    if (!data.releases.length) {
      return (
        <div
          className={cn(
            'flex size-full items-center justify-center',
            className,
          )}
          style={style}
        >
          <EmptyState hint={t(($) => $.releaseStatus.emptyState)} />
        </div>
      );
    }

    const releaseReadiness = getReleaseReadiness(
      data.releases,
      data.linearEnriched,
    );

    return (
      <div className={cn('space-y-4', className)} style={style}>
        <div className="mt-4 flex items-center justify-between">
          <h1 className="text-3xl">{t(($) => $.releaseStatus.pageTitle)}</h1>
          <div className="flex items-center justify-end">
            <RefreshIndicator
              isRefreshing={isRefreshing}
              lastRefreshedAt={lastRefreshedAt}
              isLiveRefreshEnabled={isLiveRefreshEnabled}
            />
          </div>
        </div>

        {data.releases.map((release) => (
          <ReleaseSection
            key={release.label}
            release={release}
            gitlabBaseUrl={data.gitlabBaseUrl}
            releaseReadiness={
              release.label.toLowerCase().includes('next release')
                ? (releaseReadiness ?? undefined)
                : undefined
            }
          />
        ))}
      </div>
    );
  },
);
